import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../components/ui/Table';
import ModuleForm from '../components/ModuleForm';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface Module {
  id: number;
  title: string;
  position: number;
  parent_id: number | null;
  level: string;
  published: number;
  extra: string;
  is_report: number;
  created_at: string;
  updated_at: string;
  translations?: Array<{
    id: number;
    title: string;
    lang_id: number;
    module_id: number;
  }>;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface ModulesResponse {
  items: Module[];
  pagination: PaginationInfo;
}

const Modules: React.FC = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();

  const fetchModules = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: page.toString(),
        per_page: '15',
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiService.get<ModulesResponse>('modules', params);
      if (response.success) {
        setModules(response.data.items);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchModules(currentPage, search);
    }
  }, [token, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchModules(1, search);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleExport = async () => {
    if (!hasPermission('modules', 'export')) {
      toast.error('You do not have permission to export modules.');
      return;
    }

    try {
      const params: Record<string, any> = {};
      if (search) {
        params.search = search;
      }

      await apiService.downloadFile(`modules/export?${new URLSearchParams(params)}`, `modules-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Error exporting modules:', error);
      toast.error('Error exporting modules. Please try again.');
    }
  };

  const handlePrint = () => {
    if (!hasPermission('modules', 'print')) {
      toast.error('You do not have permission to print modules.');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const tableElement = document.querySelector('.modules-table');
      if (tableElement) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Modules Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; }
                .header { text-align: center; margin-bottom: 20px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Modules Report</h1>
                <p>Generated on: ${new Date().toLocaleDateString()}</p>
              </div>
              ${tableElement.outerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const handleEditModule = (moduleId: number) => {
    if (!hasPermission('modules', 'edit')) {
      toast.error('You do not have permission to edit modules.');
      return;
    }
    const module = modules.find(m => m.id === moduleId);
    if (module) {
      setEditingModule(module);
      setShowForm(true);
    }
  };

  const handleSubmitModule = async (moduleData: Omit<Module, 'id'> & { id?: number }) => {
    setFormLoading(true);
    try {
      const response = await apiService.put(`modules/${editingModule!.id}`, moduleData);

      if (response.success) {
        setShowForm(false);
        setEditingModule(null);
        fetchModules(currentPage, search);
        toast.success('Module updated successfully');
      } else {
        throw new Error(response.message || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error('Error updating module:', error);
      throw error;
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingModule(null);
  };

  const getTranslationTitle = (module: Module) => {
    const translation = module.translations?.find(t => t.lang_id === 1); // Default language
    return translation?.title || module.title;
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Module Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Configure system modules and translations
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
              <Input
                type="text"
                placeholder="Search modules..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={loading}>
                Search
              </Button>
            </form>
            <div className="flex gap-2">
              {hasPermission('modules', 'print') && (
                <Button onClick={handlePrint} disabled={loading}>
                  Print
                </Button>
              )}
              {hasPermission('modules', 'export') && (
                <Button onClick={handleExport} disabled={loading}>
                  Export CSV
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Modules Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden modules-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Parent ID</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : modules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    No modules found
                  </TableCell>
                </TableRow>
              ) : (
                modules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>{module.id}</TableCell>
                    <TableCell>{getTranslationTitle(module)}</TableCell>
                    <TableCell>{module.position}</TableCell>
                    <TableCell>{module.parent_id}</TableCell>
                    <TableCell>{module.level}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        module.published ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {module.published ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>{new Date(module.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(module.updated_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {hasPermission('modules', 'edit') && (
                          <Button size="sm" onClick={() => handleEditModule(module.id)}>
                            Edit
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {pagination.from} to {pagination.to} of {pagination.total} results
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>
                <Button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Module Form Modal */}
        {showForm && (
          <ModuleForm
            module={editingModule}
            onSubmit={handleSubmitModule}
            onCancel={handleCancelForm}
            loading={formLoading}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default Modules; 